import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { forexApi } from '@/mocks/forexApi';

// Types
export interface ForexVendor {
  id: string;
  shopName: string;
  ownerName: string;
  email: string;
  phone: string;
  whatsapp?: string;
  location: {
    address: string;
    city: string;
    state: string;
    coordinates: { lat: number; lng: number };
  };
  businessHours: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  isVerified: boolean;
  verificationBadge: 'none' | 'basic' | 'premium';
  rating: number;
  reviewCount: number;
  avatar?: string;
  createdAt: string;
  lastActive: string;
  totalViews: number;
  totalClicks: number;
  subscriptionType: 'free' | 'premium' | 'featured';
  subscriptionExpiry?: string;
  walletBalance: number; // For ads only
  status: 'active' | 'suspended' | 'pending';
}

export interface ForexListing {
  id: string;
  vendorId: string;
  title: string;
  description: string;
  currencies: {
    from: string;
    to: string;
    buyRate: number;
    sellRate: number;
    minAmount: number;
    maxAmount: number;
  }[];
  location: {
    address: string;
    city: string;
    state: string;
    coordinates: { lat: number; lng: number };
  };
  images: string[];
  isFeatured: boolean;
  isPromoted: boolean;
  status: 'active' | 'inactive' | 'pending' | 'rejected';
  views: number;
  clicks: number;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

export interface ForexReview {
  id: string;
  listingId: string;
  vendorId: string;
  reviewerName: string;
  reviewerAvatar?: string;
  rating: number;
  comment: string;
  createdAt: string;
  isVerified: boolean;
}

export interface ForexReport {
  id: string;
  listingId: string;
  vendorId: string;
  reporterName: string;
  reporterPhone: string;
  reason: string;
  details: string;
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed';
  createdAt: string;
}

export interface ForexFilters {
  location?: string;
  city?: string;
  state?: string;
  currencyFrom?: string;
  currencyTo?: string;
  minRate?: number;
  maxRate?: number;
  verifiedOnly?: boolean;
  distance?: number;
  sortBy: 'best_rate' | 'nearest' | 'most_reviewed' | 'newest';
}

interface ForexState {
  vendors: ForexVendor[];
  listings: ForexListing[];
  reviews: ForexReview[];
  reports: ForexReport[];
  currentVendor: ForexVendor | null;
  currentListing: ForexListing | null;
  featuredListings: ForexListing[];
  searchResults: ForexListing[];
  filters: ForexFilters;
  isLoading: boolean;
  error: string | null;
  vendorDashboardStats: {
    totalViews: number;
    totalClicks: number;
    activeListings: number;
    totalReviews: number;
    averageRating: number;
    weeklyStats: { day: string; views: number; clicks: number }[];
  } | null;
}

const initialState: ForexState = {
  vendors: [],
  listings: [],
  reviews: [],
  reports: [],
  currentVendor: null,
  currentListing: null,
  featuredListings: [],
  searchResults: [],
  filters: {
    sortBy: 'best_rate',
  },
  isLoading: false,
  error: null,
  vendorDashboardStats: null,
};

// Async Thunks
export const fetchVendors = createAsyncThunk(
  'forex/fetchVendors',
  async (_, { rejectWithValue }) => {
    try {
      return await forexApi.getVendors();
    } catch (error) {
      return rejectWithValue('Failed to fetch vendors');
    }
  }
);

export const fetchListings = createAsyncThunk(
  'forex/fetchListings',
  async (filters: Partial<ForexFilters> | undefined, { rejectWithValue }) => {
    try {
      return await forexApi.getListings(filters);
    } catch (error) {
      return rejectWithValue('Failed to fetch listings');
    }
  }
);

export const fetchFeaturedListings = createAsyncThunk(
  'forex/fetchFeaturedListings',
  async (_, { rejectWithValue }) => {
    try {
      return await forexApi.getFeaturedListings();
    } catch (error) {
      return rejectWithValue('Failed to fetch featured listings');
    }
  }
);

export const fetchListingById = createAsyncThunk(
  'forex/fetchListingById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await forexApi.getListingById(id);
    } catch (error) {
      return rejectWithValue('Failed to fetch listing');
    }
  }
);

export const fetchVendorById = createAsyncThunk(
  'forex/fetchVendorById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await forexApi.getVendorById(id);
    } catch (error) {
      return rejectWithValue('Failed to fetch vendor');
    }
  }
);

export const fetchVendorListings = createAsyncThunk(
  'forex/fetchVendorListings',
  async (vendorId: string, { rejectWithValue }) => {
    try {
      return await forexApi.getVendorListings(vendorId);
    } catch (error) {
      return rejectWithValue('Failed to fetch vendor listings');
    }
  }
);

export const fetchListingReviews = createAsyncThunk(
  'forex/fetchListingReviews',
  async (listingId: string, { rejectWithValue }) => {
    try {
      return await forexApi.getListingReviews(listingId);
    } catch (error) {
      return rejectWithValue('Failed to fetch reviews');
    }
  }
);

export const createListing = createAsyncThunk(
  'forex/createListing',
  async (data: Partial<ForexListing>, { rejectWithValue }) => {
    try {
      return await forexApi.createListing(data);
    } catch (error) {
      return rejectWithValue('Failed to create listing');
    }
  }
);

export const updateListing = createAsyncThunk(
  'forex/updateListing',
  async ({ id, data }: { id: string; data: Partial<ForexListing> }, { rejectWithValue }) => {
    try {
      return await forexApi.updateListing(id, data);
    } catch (error) {
      return rejectWithValue('Failed to update listing');
    }
  }
);

export const deleteListing = createAsyncThunk(
  'forex/deleteListing',
  async (id: string, { rejectWithValue }) => {
    try {
      await forexApi.deleteListing(id);
      return id;
    } catch (error) {
      return rejectWithValue('Failed to delete listing');
    }
  }
);

export const recordListingView = createAsyncThunk(
  'forex/recordListingView',
  async (id: string, { rejectWithValue }) => {
    try {
      return await forexApi.recordView(id);
    } catch (error) {
      return rejectWithValue('Failed to record view');
    }
  }
);

export const recordListingClick = createAsyncThunk(
  'forex/recordListingClick',
  async (id: string, { rejectWithValue }) => {
    try {
      return await forexApi.recordClick(id);
    } catch (error) {
      return rejectWithValue('Failed to record click');
    }
  }
);

export const submitReview = createAsyncThunk(
  'forex/submitReview',
  async (data: Partial<ForexReview>, { rejectWithValue }) => {
    try {
      return await forexApi.submitReview(data);
    } catch (error) {
      return rejectWithValue('Failed to submit review');
    }
  }
);

export const reportListing = createAsyncThunk(
  'forex/reportListing',
  async (data: Partial<ForexReport>, { rejectWithValue }) => {
    try {
      return await forexApi.reportListing(data);
    } catch (error) {
      return rejectWithValue('Failed to submit report');
    }
  }
);

export const fetchVendorDashboardStats = createAsyncThunk(
  'forex/fetchVendorDashboardStats',
  async (vendorId: string, { rejectWithValue }) => {
    try {
      return await forexApi.getVendorDashboardStats(vendorId);
    } catch (error) {
      return rejectWithValue('Failed to fetch dashboard stats');
    }
  }
);

// Slice
const forexSlice = createSlice({
  name: 'forex',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ForexFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = { sortBy: 'best_rate' };
    },
    clearCurrentListing: (state) => {
      state.currentListing = null;
    },
    clearCurrentVendor: (state) => {
      state.currentVendor = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Vendors
      .addCase(fetchVendors.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vendors = action.payload;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Listings
      .addCase(fetchListings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchListings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(fetchListings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Featured Listings
      .addCase(fetchFeaturedListings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedListings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featuredListings = action.payload;
      })
      .addCase(fetchFeaturedListings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Listing by ID
      .addCase(fetchListingById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchListingById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentListing = action.payload;
      })
      .addCase(fetchListingById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Vendor by ID
      .addCase(fetchVendorById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVendor = action.payload;
      })
      .addCase(fetchVendorById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Listing Reviews
      .addCase(fetchListingReviews.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      // Create Listing
      .addCase(createListing.fulfilled, (state, action) => {
        state.listings.unshift(action.payload);
      })
      // Update Listing
      .addCase(updateListing.fulfilled, (state, action) => {
        const index = state.listings.findIndex(l => l.id === action.payload.id);
        if (index !== -1) {
          state.listings[index] = action.payload;
        }
        if (state.currentListing?.id === action.payload.id) {
          state.currentListing = action.payload;
        }
      })
      // Delete Listing
      .addCase(deleteListing.fulfilled, (state, action) => {
        state.listings = state.listings.filter(l => l.id !== action.payload);
        if (state.currentListing?.id === action.payload) {
          state.currentListing = null;
        }
      })
      // Submit Review
      .addCase(submitReview.fulfilled, (state, action) => {
        state.reviews.unshift(action.payload);
      })
      // Report Listing
      .addCase(reportListing.fulfilled, (state, action) => {
        state.reports.push(action.payload);
      })
      // Vendor Dashboard Stats
      .addCase(fetchVendorDashboardStats.fulfilled, (state, action) => {
        state.vendorDashboardStats = action.payload;
      });
  },
});

export const { setFilters, clearFilters, clearCurrentListing, clearCurrentVendor, clearError } = forexSlice.actions;
export default forexSlice.reducer;
